import React, {
  useEffect,
  useState,
  Fragment,
  memo,
  lazy,
  Suspense,
  useRef,
} from "react";
import ExecutionEnvironment from "exenv";
import logo from "../assets/img/logos/logo.svg";
import "./postGenerator.css";
import { Link, useLocation } from "react-router-dom";
import queryString from "query-string";

// Components
import CustomMeta from "../components/CustomMeta/CustomMeta";
const PostGrid = lazy(() =>
  import(
    /* webpackChunkName: "PostGrid" */ "../components/PostGenerator/PostGrid"
  )
);
const DesignSidebar = lazy(() =>
  import(
    /* webpackChunkName: "DesignSidebar" */ "../components/PostGenerator/DesignSidebar"
  )
);
const NavBarButtonSetup = lazy(() =>
  import(
    /* webpackChunkName: "NavBarButtonSetup" */ "../components/Layout/NavBar/NavBarButtonSetup"
  )
);
const MenuBar = lazy(() =>
  import(
    /* webpackChunkName: "MenuBar" */ "../components/TemplateBuilder/MenuBars/MenuBar"
  )
);
const VerifyEmail = lazy(() =>
  import(
    /* webpackChunkName: "VerifyEmail" */ "../components/VerifyEmail/VerifyEmail"
  )
);
const BatchContentWelcomeModal = lazy(() =>
  import(
    /* webpackChunkName: "BatchContentWelcomeModal" */ "../components/PostGenerator/BatchContentWelcomeModal"
  )
);
const URLScrapingConfirmModal = lazy(() =>
  import(
    /* webpackChunkName: "URLScrapingConfirmModal" */ "../components/PostGenerator/URLScrapingConfirmModal"
  )
);

//MUI stuff
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import TuneIcon from "@mui/icons-material/Tune";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Button from "@mui/material/Button";
import { CircularProgress, Typography } from "@mui/material";

// Redux stuff
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { setSelectedLayerId } from "../redux/actions/templateActions";
import {
  setGroupPostsBy,
  updateSinglePost,
} from "../redux/actions/designActions";
import { clearUnauthorizedBatchAccess } from "../redux/actions/userActions";

const drawerWidth = 360;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    width: "100%",
  },
  title: {
    flexGrow: 1,
  },
  logo: {
    maxWidth: "80%",
    maxHeight: 40,
    paddingTop: 5,
    paddingBottom: 5,
  },
  noHeightLink: {
    display: "block",
    lineHeight: 0,
    fontSize: 0,
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    overflowX: "hidden",
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  },
  content: {
    flexGrow: 1,
    display: "flex",
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
    backgroundColor: theme.palette.lightGrey.main,
    position: "relative",
    height: "calc(100vh - 64px)",
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  navbar: {
    backgroundColor: "white",
  },
  progressDiv: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    textAlign: "center",
    paddingTop: 150,
  },
  mainArea: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflow: "hidden",
  },
  noticeText: {
    padding: 10,
    backgroundColor: "#916DB047",
    borderRadius: 5,
  },
  menuBar: {
    backgroundColor: theme.palette.darkGrey.main,
    position: "sticky",
    top: 0,
    zIndex: 10,
    flexShrink: 0,
  },
}));

function postGenerator(props) {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const gridRef = useRef();

  const [open, setOpen] = React.useState(true);
  const [progress, setProgress] = React.useState(0);
  const [showBatchWelcomeModal, setShowBatchWelcomeModal] = useState(false);
  const [batchInfo, setBatchInfo] = useState(null);
  const [showURLScrapingModal, setShowURLScrapingModal] = useState(false);
  const [pendingScrapingURL, setPendingScrapingURL] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [pendingBatchId, setPendingBatchId] = useState(null);
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const [unauthorizedBatchId, setUnauthorizedBatchId] = useState(null);

  const clickAwayTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(clickAwayTimerRef.current);
    };
  }, []);

  // Listen for URL scraping requests from DesignSidebar
  useEffect(() => {
    const handleScrapingRequest = (event) => {
      const { url } = event.detail;

      // Always show confirmation modal
      setPendingScrapingURL(url);
      setShowURLScrapingModal(true);
    };

    window.addEventListener("requestURLScraping", handleScrapingRequest);

    return () => {
      window.removeEventListener("requestURLScraping", handleScrapingRequest);
    };
  }, []);

  // Track the current batch ID and whether modal has been opened for it
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [hasOpenedModalForBatch, setHasOpenedModalForBatch] = useState(false);

  // Check for batchId when user is not authenticated
  useEffect(() => {
    const params = queryString.parse(location.search);

    if (params.batchId && !props.authenticated) {
      // User has a batchId but is not authenticated - show login prompt
      setPendingBatchId(params.batchId);
      setShowLoginPrompt(true);
    } else if (params.batchId && props.authenticated && showLoginPrompt) {
      // User was prompted to login and is now authenticated - hide login prompt
      setShowLoginPrompt(false);
      setPendingBatchId(null);
    } else if (!params.batchId) {
      // No batchId - hide login prompt and unauthorized modal if they were showing
      setShowLoginPrompt(false);
      setPendingBatchId(null);
      setShowUnauthorizedModal(false);
      setUnauthorizedBatchId(null);
      // Clear batch welcome modal state as well
      setShowBatchWelcomeModal(false);
      setBatchInfo(null);
      // Clear unauthorized batch state from Redux
      props.clearUnauthorizedBatchAccess();
    }
  }, [location.search, props.authenticated, showLoginPrompt]);

  // Check for unauthorized batch access
  useEffect(() => {
    if (props.unauthorizedBatchId) {
      setUnauthorizedBatchId(props.unauthorizedBatchId);
      setShowUnauthorizedModal(true);
      // Hide batch welcome modal if it was showing
      setShowBatchWelcomeModal(false);
      setBatchInfo(null);
    }
  }, [props.unauthorizedBatchId]);

  // Check for batch content and show welcome modal
  useEffect(() => {
    const params = queryString.parse(location.search);

    if (
      params.batchId &&
      props.posts.length > 0 &&
      !props.unauthorizedBatchId
    ) {
      // Check if user has opted out of seeing the modal
      const hideBatchModal = localStorage.getItem("hideBatchWelcomeModal");

      // If this is a new batch ID, reset the modal opened flag and set grouping to date
      if (currentBatchId !== params.batchId) {
        setHasOpenedModalForBatch(false);
        setCurrentBatchId(params.batchId);

        // Automatically set grouping to "date" for batch content
        props.setGroupPostsBy("date");
      }

      // Show modal if user hasn't opted out and hasn't opened it for this batch yet
      if (!hideBatchModal && !hasOpenedModalForBatch) {
        // Calculate batch info
        const firstPost = props.posts.find((post) => post.scheduledPostDate);
        const startDate = firstPost
          ? new Date(firstPost.scheduledPostDate).toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : null;

        // Calculate platform breakdown
        const platformBreakdown = {};
        props.posts.forEach((post) => {
          // Get platform from template, fallback to post.platform for backward compatibility
          const platform =
            post.template?.platform || post.platform || "pinterest";
          const key = `${platform}:${
            post.socialMediaProfileUsername || "Unassigned"
          }`;
          platformBreakdown[key] = (platformBreakdown[key] || 0) + 1;
        });

        setBatchInfo({
          batchId: params.batchId,
          totalPosts: props.posts.length,
          startDate,
          platformBreakdown,
        });

        setShowBatchWelcomeModal(true);
        setHasOpenedModalForBatch(true);
      }
    } else if (!params.batchId) {
      // Reset batch info and modal state when no batchId is present
      setBatchInfo(null);
      setShowBatchWelcomeModal(false);
      setCurrentBatchId(null);
      setHasOpenedModalForBatch(false);
    }
  }, [
    props.posts,
    location.search,
    props.groupPostsBy,
    props.unauthorizedBatchId,
  ]);

  useEffect(() => {
    if (!props.isBulkScheduling) return;

    setProgress(0);

    let totalPosts = props.posts.length;
    let percentagePerPost = 90 / totalPosts;
    let smoothingFactor = 6;

    const interval = setInterval(() => {
      setProgress((currentProgress) => {
        const newProgress =
          currentProgress >= 90
            ? 90
            : Math.ceil(currentProgress + percentagePerPost / smoothingFactor);

        if (newProgress >= 90) {
          clearInterval(interval);
        }

        return newProgress;
      });
    }, 4000 / smoothingFactor);

    return () => {
      clearInterval(interval);
    };
  }, [props.isBulkScheduling]);

  useEffect(() => {
    if (gridRef.current != null) {
      gridRef.current.resetAfterIndices({
        columnIndex: 0,
        rowIndex: 0,
        shouldForceUpdate: true,
      });
    }
  }, [props.zoom]);

  useEffect(() => {
    if (gridRef.current != null) {
      gridRef.current.resetAfterRowIndex(0);
    }
  }, [props.posts.length]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleLoginRedirect = () => {
    // Store the current URL to redirect back after login
    localStorage.setItem(
      "redirectAfterLogin",
      window.location.pathname + window.location.search
    );
    window.location.href = "/login";
  };

  const handleSignupRedirect = () => {
    // Store the current URL to redirect back after signup
    localStorage.setItem(
      "redirectAfterLogin",
      window.location.pathname + window.location.search
    );
    window.location.href = "/signup";
  };

  const handleClearScheduledDates = () => {
    // Clear scheduled dates from all posts that have them
    props.posts.forEach((post, index) => {
      if (post.type === "SpecificDate" && post.scheduledPostDate) {
        props.updateSinglePost(index, {
          type: "Queue", // Set to Queue so it uses scheduling settings
          scheduledPostDate: null, // Clear the scheduled date
        });
      }
    });

    // Show a success message or feedback to user
    console.log(
      "Scheduled dates cleared for all posts - now using Queue scheduling"
    );
  };

  const getPostGridMarkup = () => {
    let postGridMarkup;

    if (props.authenticated && props.isEmailVerified === false) {
      postGridMarkup = <VerifyEmail />;
    } else if (
      props.isGenerating ||
      props.loadingUser ||
      props.isLoadingDesignSettings ||
      props.isBatchContentLoading
    ) {
      postGridMarkup = (
        <div className={classes.progressDiv}>
          <CircularProgress size={25} />
        </div>
      );
    } else {
      postGridMarkup = (
        <PostGrid
          gridRef={gridRef}
          posts={props.posts}
          isBulkScheduling={props.isBulkScheduling}
          progress={progress}
          isRandomTemplates={props.isRandomTemplates}
        />
      );
    }

    return postGridMarkup;
  };

  return (
    <div className={classes.root}>
      <CustomMeta
        title={`Generate Social Media Content - Content Generator`}
        description={`Create high-converting posts with our AI-powered design tool. Generate eye-catching visuals, craft viral-worthy descriptions, and schedule content for maximum engagement. Perfect for e-commerce, blogs, and affiliate marketing.`}
        url={`https://contentgenerator.io/generate`}
      />
      {ExecutionEnvironment.canUseDOM && (
        <Suspense fallback={<div />}>
          <CssBaseline />
          <AppBar
            position="fixed"
            className={`${cx(classes.appBar, {
              [classes.appBarShift]: open,
            })} ${classes.navbar}`}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                className={cx(classes.menuButton, open && classes.hide)}
                size="large"
              >
                <TuneIcon color="primary" />
              </IconButton>
              <div className={classes.title}>
                <Link to="/" className={classes.noHeightLink}>
                  <img src={logo} alt="logo" className={classes.logo} />{" "}
                </Link>
              </div>

              <NavBarButtonSetup />
            </Toolbar>
          </AppBar>
          <Drawer
            className={classes.drawer}
            variant="persistent"
            anchor="left"
            open={open}
            classes={{
              paper: classes.drawerPaper,
            }}
          >
            <div className={classes.drawerHeader}>
              {theme.direction === "ltr" ? (
                <Button
                  color="primary"
                  className={classes.hideButton}
                  startIcon={<ChevronLeftIcon />}
                  onClick={handleDrawerClose}
                >
                  Hide
                </Button>
              ) : (
                <IconButton onClick={handleDrawerClose} size="large">
                  <ChevronRightIcon />
                </IconButton>
              )}
            </div>
            <DesignSidebar hideDesignSidebar={handleDrawerClose} />
          </Drawer>
          <main
            className={cx(classes.content, {
              [classes.contentShift]: open,
            })}
          >
            <div className={classes.mainArea}>
              <div className={classes.menuBar}>
                <MenuBar />
              </div>
              {/* <Typography className={classes.noticeText}>
                PLEASE NOTE: Google is having issues with their us-central1
                server which we are hosting on. We have deployed the app to a US
                server in the mean time to mitigate the issue. We are still
                experiencing some issues in the app. Thanks for your patience.
              </Typography> */}
              {getPostGridMarkup()}
            </div>
          </main>
        </Suspense>
      )}

      {/* Batch Content Welcome Modal */}
      {batchInfo && (
        <Suspense fallback={<div />}>
          <BatchContentWelcomeModal
            open={showBatchWelcomeModal}
            onClose={() => setShowBatchWelcomeModal(false)}
            onContinue={() => setShowBatchWelcomeModal(false)}
            totalPosts={batchInfo.totalPosts}
            platformBreakdown={batchInfo.platformBreakdown}
            batchId={batchInfo.batchId}
            startDate={batchInfo.startDate}
            posts={props.posts}
            onClearScheduledDates={handleClearScheduledDates}
          />
        </Suspense>
      )}

      {/* URL Scraping Confirmation Modal */}
      {pendingScrapingURL && (
        <Suspense fallback={<div />}>
          <URLScrapingConfirmModal
            open={showURLScrapingModal}
            onClose={() => {
              setShowURLScrapingModal(false);
              setPendingScrapingURL(null);
            }}
            onConfirm={() => {
              setShowURLScrapingModal(false);
              // Trigger scraping by dispatching a custom event
              window.dispatchEvent(
                new CustomEvent("confirmURLScraping", {
                  detail: { url: pendingScrapingURL },
                })
              );
              setPendingScrapingURL(null);
            }}
            onReject={() => {
              setShowURLScrapingModal(false);
              // Trigger normal loading without scraping
              window.dispatchEvent(
                new CustomEvent("rejectURLScraping", {
                  detail: { url: pendingScrapingURL },
                })
              );
              setPendingScrapingURL(null);
            }}
            url={pendingScrapingURL}
          />
        </Suspense>
      )}

      {/* Login Prompt Modal for Batch Content */}
      {pendingBatchId && (
        <Suspense fallback={<div />}>
          <BatchContentWelcomeModal
            open={showLoginPrompt}
            onClose={() => setShowLoginPrompt(false)}
            onContinue={() => setShowLoginPrompt(false)}
            requiresLogin={true}
            onLogin={handleLoginRedirect}
            onSignup={handleSignupRedirect}
            batchId={pendingBatchId}
          />
        </Suspense>
      )}

      {/* Unauthorized Access Modal for Batch Content */}
      {unauthorizedBatchId && (
        <Suspense fallback={<div />}>
          <BatchContentWelcomeModal
            open={showUnauthorizedModal}
            onClose={() => {
              setShowUnauthorizedModal(false);
              setUnauthorizedBatchId(null);
            }}
            onContinue={() => {
              setShowUnauthorizedModal(false);
              setUnauthorizedBatchId(null);
            }}
            isUnauthorized={true}
            batchId={unauthorizedBatchId}
          />
        </Suspense>
      )}
    </div>
  );
}

postGenerator.propTypes = {
  posts: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  posts: state.design.posts,
  isGenerating: state.design.isGenerating,
  postOverrides: state.design.postOverrides,
  isBulkScheduling: state.data.isBulkScheduling,
  zoom: state.templates.present.zoom,
  isRandomTemplates: state.design.isRandomTemplates,
  loadingUser: state.user.loading,
  isLoadingDesignSettings: state.design.isLoadingDesignSettings,
  isBatchContentLoading: state.design.isBatchContentLoading,
  selectedLayerId: state.templates.present.selectedLayerId,
  authenticated: state.user.authenticated,
  isEmailVerified: state.user.credentials.isEmailVerified,
  groupPostsBy: state.design.groupPostsBy,
  unauthorizedBatchId: state.design.unauthorizedBatchId,
});

export default connect(mapStateToProps, {
  setSelectedLayerId,
  setGroupPostsBy,
  clearUnauthorizedBatchAccess,
  updateSinglePost,
})(postGenerator);
